import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router'
import axios from 'axios'
import { useEffectX } from 'use-effect-x'
import { Loader, Pagination, Input, InputGroup, Icon } from 'rsuite'
import { useTranslation } from 'react-i18next'

import ProductsList from './products/list'
import ProductsFilters from './products/filters'
import MaterialsLegend from './product/materials-legend'

import configData from './../config.json'


const PageProducts = (props) => {
  
  // define component states
  const [ products, setProducts ] = useState(null)
  const [ filters, setFilters ] = useState({})

  const [ applyInitialCat, setApplyInitialCat ] = useState(true)

  const [ categories, setCategories ] = useState(null)
  const [ subCategories, setSubCategories ] = useState(null)
  const [ purposes, setPurposes ] = useState(null)
  const [ intendedMaterials, setIntendedMaterials ] = useState(null)
  const [ searchString, setSearchString ] = useState('')

  const [ sortedProducts, setSortedProducts ] = useState(null)

  const [ productsPerPage, setProductsPerPage ] = useState(20)
  const [ paginatedProducts, setPaginatedProducts ] = useState({
    'products': [],
    'page': 1,
    'pages_count': 1
  })

  const { t, i18n } = useTranslation()
  const lang = i18n.language

  const history = useHistory()

  // get category from params
  useEffect( () => {
    if (applyInitialCat) {
      const params = new URLSearchParams(props.location.search)
      const cat = params.get('cat')
      if (cat) {
        setFilters({
         category: [parseInt(cat)] 
        })
      }
      setApplyInitialCat(false)
    }
  }, [applyInitialCat, props.location.search] )
  
  // store filters 
  useEffectX(
    ({changedItem: [filtersObj]}) => {
      if (filtersObj.changed) {
        sessionStorage.setItem('filters', filters)
      }
      if (history.location.state) {
        setFilters(history.location.state)
      }
    }, [filters]
  )

  // get products
  useEffectX(
    ({changedItem: [productsObj]}) => {
      if (productsObj.isFirstRun) {
        axios.get(`${configData['REST_URL']}/acf/v3/products?per_page=9999`)
        .then(productsData => {
          setProducts(productsData.data)
        })
      } 
    },[products]
  )
  // get categories
  useEffectX(
    ({changedItem: [categoriesObj]}) => {
      if (categoriesObj.isFirstRun) {
        axios.get(`${configData['REST_URL']}/wp/v2/category?per_page=100`)
        .then(categoriesData => {
          setCategories(categoriesData.data)
        })
      } 
    },[categories]
  )
  // get sub categories
  useEffectX(
    ({changedItem: [subCategoriesObj]}) => {
      if (subCategoriesObj.isFirstRun) {
        axios.get(`${configData['REST_URL']}/wp/v2/subcategory?per_page=100`)
        .then(subCategoriesData => {
          setSubCategories(subCategoriesData.data)
        })
      }
    }, [subCategories]
  )
  // get purposes
  useEffectX(
    ({changedItem: [purposesObj]}) => {
      if (purposesObj.isFirstRun) {
        axios.get(`${configData['REST_URL']}/wp/v2/purpose?per_page=100`)
        .then(purposesData => {
          setPurposes(purposesData.data)
        })
      }
    }, [purposes]
  )
  //  get intended materials
  useEffectX(
    ({changedItem: [intendedMaterialsObj]}) => {
      if (intendedMaterialsObj.isFirstRun) {
        axios.get(`${configData['REST_URL']}/wp/v2/intended_material?per_page=100`)
        .then(intendedMaterialsData => {
          setIntendedMaterials(intendedMaterialsData.data)
        })
      }
    }, [intendedMaterials]
  )

  // paginate pages
  useEffectX(
    ({changedItem: [paginatedProductsObj]}) => {
      if (paginatedProductsObj.changed && paginatedProductsObj.next?.products.length > 0) {        
        showPaginatedProducts()
      }
    }, [paginatedProducts]
  )

  useEffectX(
    ({changedItem: [searchStringObj]}) => {
      if (searchStringObj.changed && !searchStringObj.isFirstRun) {
        applyFilters()
      }
    }, [searchString]
  )

  const filteredProducts = () => {
    if (Object.keys(filters).length === 0) {
      return products
    } 
    
    let filtered = products
    for (const [key, value] of Object.entries(filters)) {
      filtered = filtered.filter(product => {
        if (key === 'intended') {
          return value.every(r => {
            if (product.acf.intended === false) return false
            return product.acf?.intended?.includes(r)
          })
          // return value.every(r => product.acf.intended?.includes(r))
        } else {
          return value.includes(product.acf.extra[key])
        }
      })
    }
    return filtered
  }

  const sortProductsByCategories = () => {
    const sRawProducts = []
    const f1Products = filteredProducts()
    const fProducts = f1Products.filter( product => {
      return product.acf.names[`name_${lang}`].toLowerCase().includes(searchString.toLowerCase())
    } )
    //  sort for pagination
    categories.forEach(category => {
      const categoryProducts = []
      const productsFromCategory = fProducts.filter(product => {
        return product.acf.extra.category === category.id
      })
      if (productsFromCategory.length > 0) {
        productsFromCategory.map(product => sRawProducts.push(product))
      }
    })
    paginatedProducts.products = sRawProducts
    setPaginatedProducts({
      'products': sRawProducts,
      'page': 1,
      'pages_count': Math.ceil(sRawProducts.length / productsPerPage)
    })
    showPaginatedProducts()
  }

  const showPaginatedProducts = () => {

    const { products, page } = paginatedProducts
    
    const visibleProducts = products.slice(productsPerPage * (page - 1), productsPerPage * page)

    const sProducts = []
    // make final list
    categories.forEach(category => {
      const categoryProducts = []
      const productsFromCategory = visibleProducts.filter(product => {
        return product.acf.extra.category === category.id
      })
      if (productsFromCategory.length > 0) {
        sProducts.push({
          'id': category.id,
          'products':productsFromCategory
        })
      }
    })
    setSortedProducts(sProducts)    
  }
  
  if (categories !== null && 
    subCategories !== null && 
    products !== null && 
    purposes !== null &&
    intendedMaterials !== null &&
    sortedProducts === null
    ) {
    sortProductsByCategories()
  }

  const handleFilter = (recivedFilters) => {
    if (recivedFilters.data.length === 0) {
      delete(filters[recivedFilters.taxonomy])
    } else {
      filters[recivedFilters.taxonomy] = recivedFilters.data
    }
    setFilters({...filters})
  }

  const applyFilters = () => {
    sortProductsByCategories()
  }

  const handlePageSelect = (page) => {
    paginatedProducts.page = page
    setPaginatedProducts({...paginatedProducts})
  }

  return (
    <div className="page page-products">
      <div className="content">
        <ProductsFilters
          handleFilters={handleFilter}
          categories={categories}
          purposes={purposes}
          intendedMaterials={intendedMaterials}
          initialFilters={filters}
          applyFilters={applyFilters}
        />
        { sortedProducts ? (
          <>
            <div className="products-toolbar">
              <InputGroup
                inside              
                className="search"
              >
                <Input
                  value={searchString}
                  onChange={v => setSearchString(v)}
                  placeholder={t("Search...")}
                />
                {searchString.length > 0 ? (
                  <InputGroup.Button>
                    <Icon icon="close" onClick={() => setSearchString('')}/>
                  </InputGroup.Button>
                ) : (
                  <InputGroup.Button>
                    <Icon icon="search"/>
                  </InputGroup.Button>
                )}
              </InputGroup>
            </div>
            <ProductsList
              products={sortedProducts}
              categories={categories}
              subCategories={subCategories}
              purposes={purposes}
              intendedMaterials={intendedMaterials}
              filters={filters}
            />        
            <div className="products-pagination">
              <Pagination
                prev
                next
                first
                last
                elipsis
                pages={paginatedProducts.pages_count}
                maxButtons={5}
                activePage={paginatedProducts.page}
                onSelect={handlePageSelect}
              />
            </div>  
            <MaterialsLegend/>
          </>
        ) : (
        <div className="products-list">
          <div className="products-list-empty">
          <Loader size="lg" content={t("Loading...")} vertical/>
          </div>
        </div>
        ) }
      </div>
    </div>
  )
}

export default PageProducts